import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import DarkModeToggle from 'front/components/ui/DarkModeToggle';
import { useTheme } from '../../../core/contexts/ThemeContext';

const Container = styled.div`
  position: relative;
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  pointer-events: none;
  background:
    url('/images/global/member_bg_bottom1.png') no-repeat left bottom,
    url('/images/global/member_bg_bottom2.png') no-repeat right bottom,
    url('/images/global/member_bg.png') no-repeat center center;
  background-size: auto, auto, cover;

  html.dark-mode & {
    background:
      url('/images/global/member_bg_bottom1_dark.png') no-repeat left bottom,
      url('/images/global/member_bg_bottom2_dark.png') no-repeat right bottom,
      url('/images/global/member_bg_dark.png') no-repeat center center;
  }
`;

const Header = styled.header`
  position: absolute;
  left: 0;
  top: 0;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: ${props => props.theme.spacing.xl} ${props => props.theme.spacing.xl};
  & button {
    pointer-events: auto;
  }
`;

const Logo = styled.div`
  cursor: pointer;
  pointer-events: auto;

  html.dark-mode & img {
    filter: invert(1);
  }
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 320px;
  gap: ${props => props.theme.spacing['4xl']};
  pointer-events: auto;
`;

const Title = styled.h1`
  font-size: ${props => props.theme.fontSize['2xl']};
  font-weight: ${props => props.theme.fontWeight.semibold};
  color: ${props => props.theme.colors.book.color1b};
  text-align: center;
`;

const TermsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
  width: 100%;
  max-width: 600px;
`;

const TermItem = styled.div`
  background-color: var(--bg-secondary);
  border-radius: ${props => props.theme.borderRadius.xl};
  padding: ${props => props.theme.spacing['2xl']};
  box-shadow: ${props => props.theme.shadows.sm};
  display: flex;
  flex-direction: column;
`;

const Checkbox = styled.div<{ $checked: boolean; $disabled: boolean }>`
  width: 18px;
  height: 18px;
  border-radius: ${props => props.theme.borderRadius.sm};
  border: 1px solid ${props =>
    props.$disabled ? props.theme.colors.gray[300] :
    props.$checked ? props.theme.colors.toss.blue : props.theme.colors.book.color4b};
  background-color: ${props =>
    props.$disabled ? props.theme.colors.gray[100] :
    props.$checked ? props.theme.colors.toss.blue : '#F4F4F4'};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  flex-shrink: 0;
  transition: all 0.2s;
  opacity: ${props => props.$disabled ? 0.5 : 1};

  &::after {
    content: '';
    width: 3px;
    height: 8px;
    border: solid ${props =>
      props.$disabled ? props.theme.colors.gray[300] :
      props.$checked ? 'white' : props.theme.colors.book.color4b};
    border-width: 0 2px 2px 0;
    border-radius: 2px;
    transform: rotate(45deg) translateY(0px) translateX(-1px);
  }
`;

const TermHeader = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
`;

const TermContent = styled.div<{ $expanded: boolean }>`
  max-height: ${props => props.$expanded ? '384px' : '0'};
  overflow-y: auto;
  transition: max-height 0.3s ease-in-out;
`;

const AccordionInner = styled.div`
  padding-top: ${props => props.theme.spacing['3xl']};
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.normal};
  color: ${props => props.theme.colors.book.color6b};
  line-height: 1.8;
  white-space: pre-line;
`;

const TermLabel = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const TermText = styled.span`
  font-size: ${props => props.theme.fontSize.base};
  color: var(--text-primary);
  font-weight: ${props => props.theme.fontWeight.medium};
`;


const ViewButton = styled.button<{ $expanded: boolean }>`
  margin-left: auto;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.sm};
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  outline: none;
  &::after {
    content: '';
    display: inline-block;
    width: 7px;
    height: 7px;
    border-right: 2px solid #4B4B4B;
    border-bottom: 2px solid #4B4B4B;
    transform: ${props => props.$expanded ? 'rotate(-135deg) translateY(0)' : 'rotate(45deg) translateY(-3px)'};
    transition: transform 0.2s;
  }
`;


const NextButton = styled.button<{ $disabled: boolean }>`
  width: 100%;
  max-width: 600px;
  height: 44px;
  padding: ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: none;
  background-color: ${props => props.$disabled ? props.theme.colors.book.color0b : props.theme.colors.toss.blue};
  color: ${props => props.$disabled ? props.theme.colors.white : '#fff'};
  font-size: ${props => props.theme.fontSize.base};
  font-weight: ${props => props.theme.fontWeight.semibold};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;
  box-shadow: ${props => props.$disabled ? 'none' : props.theme.shadows.sm};

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: ${props => props.theme.shadows.md};
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

interface Term {
  id: string;
  title: string;
  required: boolean;
  content: string;
  viewed: boolean;
  checked: boolean;
}

const RegisterTermsAgreement: React.FC = () => {
  const navigate = useNavigate();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const [terms, setTerms] = useState<Term[]>([
    {
      id: 'terms',
      title: '이용약관',
      required: true,
      content: `제1조(목적)
이 약관은 듬뿍(이하 "회사"라 함)이 제공하는 서비스의 이용과 관련하여 회사와 이용자 간의 권리, 의무 및 책임사항을 규정함을 목적으로 합니다.

제2조(정의)
1. "서비스"란 회사가 제공하는 모든 온라인 서비스를 의미합니다.
2. "이용자"란 이 약관에 따라 회사가 제공하는 서비스를 받는 회원 및 비회원을 의미합니다.
3. "회원"이란 회사에 개인정보를 제공하여 회원등록을 한 자로서, 회사의 정보를 지속적으로 제공받으며, 회사가 제공하는 서비스를 계속적으로 이용할 수 있는 자를 의미합니다.

제3조(약관의 게시와 개정)
1. 회사는 이 약관의 내용을 이용자가 쉽게 알 수 있도록 서비스 초기 화면에 게시합니다.
2. 회사는 필요한 경우 관련 법령을 위배하지 않는 범위에서 이 약관을 개정할 수 있습니다.`,
      viewed: false,
      checked: false,
    },
    {
      id: 'privacy',
      title: '개인정보 보호방침',
      required: true,
      content: `제1조(개인정보의 처리목적)
회사는 다음의 목적을 위하여 개인정보를 처리합니다. 처리하고 있는 개인정보는 다음의 목적 이외의 용도로는 이용되지 않으며, 이용 목적이 변경되는 경우에는 개인정보 보호법 제18조에 따라 별도의 동의를 받는 등 필요한 조치를 이행할 예정입니다.

1. 서비스 제공
- 회원 가입의사 확인, 회원제 서비스 제공에 따른 본인 식별·인증, 회원자격 유지·관리, 서비스 부정이용 방지 목적으로 개인정보를 처리합니다.

2. 민원사무 처리
- 민원인의 신원 확인, 민원사항 확인, 사실조사를 위한 연락·통지, 처리결과 통보의 목적으로 개인정보를 처리합니다.

제2조(개인정보의 처리 및 보유기간)
1. 회사는 법령에 따른 개인정보 보유·이용기간 또는 정보주체로부터 개인정보를 수집 시에 동의받은 개인정보 보유·이용기간 내에서 개인정보를 처리·보유합니다.`,
      viewed: false,
      checked: false,
    },
  ]);
  const [expandedTermId, setExpandedTermId] = useState<string | null>(null);

  const handleTermView = (termId: string) => {
    const isExpanding = expandedTermId !== termId;
    setExpandedTermId(isExpanding ? termId : null);
    if (isExpanding) {
      setTerms(prev =>
        prev.map(term =>
          term.id === termId ? { ...term, viewed: true } : term
        )
      );
    }
  };

  const handleTermToggle = (termId: string) => {
    const term = terms.find(t => t.id === termId);
    if (!term || !term.viewed) return;
    setTerms(prev =>
      prev.map(t =>
        t.id === termId ? { ...t, checked: !t.checked } : t
      )
    );
  };

  const handleNext = () => {
    const allRequiredTermsChecked = terms
      .filter(term => term.required)
      .every(term => term.checked);
    
    if (allRequiredTermsChecked) {
      navigate('/register/form');
    }
  };

  const allRequiredTermsChecked = terms
    .filter(term => term.required)
    .every(term => term.checked);

  return (
    <Container>
      <Header>
        <Logo onClick={() => navigate('/')}><img src="/images/global/logo.svg" alt="logo" /></Logo>
        <DarkModeToggle isDark={isDarkMode} onToggle={toggleDarkMode} />
      </Header>
      <Content>
        <Title>회원가입</Title>
        <TermsList>
          {terms.map(term => (
            <TermItem key={term.id}>
              <TermHeader>
                <Checkbox
                  $checked={term.checked}
                  $disabled={!term.viewed}
                  onClick={() => handleTermToggle(term.id)}
                />
                <TermLabel>
                  <TermText>
                    {term.required ? '(필수)' : '(선택)'} {term.title}
                  </TermText>
                </TermLabel>
                <ViewButton $expanded={expandedTermId === term.id} onClick={() => handleTermView(term.id)}>
                </ViewButton>
              </TermHeader>
              <TermContent $expanded={expandedTermId === term.id}>
                <AccordionInner>{term.content}</AccordionInner>
              </TermContent>
            </TermItem>
          ))}
        </TermsList>
        <NextButton $disabled={!allRequiredTermsChecked} onClick={handleNext}>
          다음
        </NextButton>
      </Content>
    </Container>
  );
};

export default RegisterTermsAgreement;
